import { useEffect, useState } from "react";
import { Percent, Save, Building2, Mail, MapPin } from "lucide-react";
import client, { extractError } from "@/api";

const percentFromBps = (bps) =>
  bps === null || bps === undefined || bps === "" ? "" : String(bps / 100);

const IDENTITY_FIELDS = [
  "legal_name", "brand_name", "pan", "gstin",
  "address_line1", "address_line2", "city", "district",
  "state", "state_code", "pin_code", "country",
  "email", "phone", "website",
];

const emptyIdentity = () => {
  const o = {};
  for (const f of IDENTITY_FIELDS) o[f] = "";
  // Internal form state carries the human-facing percent (e.g. 18 or 18.00).
  // Converted to `gst_rate_bps` on save (× 100) and from bps on load (÷ 100).
  o.gst_rate_percent = "18";
  return o;
};

/* ---------- Facilitation fees (Buyer + Seller pair) ---------- */
function FacilitationFeeCard() {
  const [setting, setSetting] = useState(null);
  const [buyerPercent, setBuyerPercent] = useState("");
  const [sellerPercent, setSellerPercent] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    setError("");
    try {
      const r = await client.get("/admin/settings/facilitation-fee-pair");
      setSetting(r.data);
      setBuyerPercent(percentFromBps(r.data.buyer_fee_rate_bps));
      setSellerPercent(percentFromBps(r.data.seller_fee_rate_bps));
    } catch (err) {
      setError(extractError(err, "Unable to load platform settings."));
    }
  };
  useEffect(() => { load(); }, []);

  const validPercent = (raw) => {
    const n = Number(raw);
    return !Number.isNaN(n) && n >= 0 && n <= 50;
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setMessage("");
    try {
      if (!validPercent(buyerPercent)) {
        setError("Buyer facilitation fee must be between 0% and 50%.");
        setBusy(false); return;
      }
      if (!validPercent(sellerPercent)) {
        setError("Seller facilitation fee must be between 0% and 50%.");
        setBusy(false); return;
      }
      const payload = {
        buyer_fee_rate_bps: Math.round(Number(buyerPercent) * 100),
        seller_fee_rate_bps: Math.round(Number(sellerPercent) * 100),
      };
      const r = await client.put("/admin/settings/facilitation-fee-pair", payload);
      setSetting(r.data);
      setMessage(
        `Facilitation fees updated: buyer ${r.data.buyer_fee_rate_percent}% · seller ${r.data.seller_fee_rate_percent}%. Applies to future transactions.`
      );
    } catch (err) {
      setError(extractError(err, "Unable to update platform settings."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="section-block" data-testid="facilitation-fee-card">
      <div className="section-heading">
        <div>
          <p className="eyebrow">FACILITATION FEES</p>
          <h3>Buyer &amp; Seller Rates</h3>
        </div>
      </div>
      {error && <div className="form-error" data-testid="platform-settings-error">{error}</div>}
      {message && <div className="form-success" data-testid="platform-settings-success">{message}</div>}
      <section className="detail-grid" data-testid="platform-settings-current">
        <article>
          <p className="eyebrow">CURRENT BUYER FEE</p>
          <p><strong data-testid="current-buyer-fee-percent">
            {setting?.buyer_fee_rate_percent ?? "—"}%
          </strong></p>
        </article>
        <article>
          <p className="eyebrow">CURRENT SELLER FEE</p>
          <p><strong data-testid="current-seller-fee-percent">
            {setting?.seller_fee_rate_percent ?? "—"}%
          </strong></p>
        </article>
        <article>
          <p className="eyebrow">GST (FIXED)</p>
          <p><strong>{setting?.gst_rate_percent ?? 18}%</strong></p>
        </article>
        <article>
          <p className="eyebrow">LAST UPDATED BY</p>
          <p>{setting?.updated_by || "—"}</p>
        </article>
      </section>
      <form className="company-form" onSubmit={save} data-testid="platform-settings-form">
        <div className="form-grid">
          <label>Buyer facilitation fee (%)
            <input type="number" min={0} max={50} step={0.01} required
              value={buyerPercent} onChange={(e) => setBuyerPercent(e.target.value)}
              data-testid="buyer-fee-percent-input" />
          </label>
          <label>Seller facilitation fee (%)
            <input type="number" min={0} max={50} step={0.01} required
              value={sellerPercent} onChange={(e) => setSellerPercent(e.target.value)}
              data-testid="seller-fee-percent-input" />
          </label>
        </div>
        <p className="muted small" data-testid="applies-to-note">
          <Percent size={12} /> Buyer and seller rates are configured independently.
          Rates apply to <strong>future transactions</strong> only — existing
          settled transactions retain their originally snapshotted fee.
        </p>
        <div className="form-actions">
          <button type="submit" className="primary-button" disabled={busy}
                   data-testid="save-fee-pair-button">
            <Save size={13} /> {busy ? "Saving…" : "Save fees"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------- Billing Identity (Biz Angels legal entity) ---------- */
function BillingIdentityCard() {
  const [ident, setIdent] = useState(emptyIdentity());
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    setError("");
    try {
      const r = await client.get("/admin/settings/billing-identity");
      const next = emptyIdentity();
      for (const f of IDENTITY_FIELDS) next[f] = r.data[f] || "";
      // Backend stores basis points (1800 = 18%). UI shows percent.
      const bps = r.data.gst_rate_bps ?? 1800;
      next.gst_rate_percent = String(bps / 100);
      setIdent(next);
      setMeta({ configured: r.data.configured, gst_rate_percent: r.data.gst_rate_percent });
    } catch (err) {
      setError(extractError(err, "Unable to load billing identity."));
    }
  };
  useEffect(() => { load(); }, []);

  const change = (field) => (e) =>
    setIdent((prev) => ({ ...prev, [field]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setMessage("");
    try {
      const gstPercent = Number(ident.gst_rate_percent);
      if (!Number.isFinite(gstPercent) || gstPercent < 0 || gstPercent > 100) {
        setError("GST rate must be between 0% and 100%.");
        setBusy(false); return;
      }
      const payload = {
        legal_name: ident.legal_name.trim(),
        brand_name: ident.brand_name.trim() || null,
        pan: ident.pan.trim() || null,
        gstin: ident.gstin.trim().toUpperCase(),
        address_line1: ident.address_line1.trim(),
        address_line2: ident.address_line2.trim() || null,
        city: ident.city.trim(),
        district: ident.district.trim() || null,
        state: ident.state.trim(),
        state_code: ident.state_code.trim(),
        pin_code: ident.pin_code.trim(),
        country: ident.country.trim(),
        email: ident.email.trim() || null,
        phone: ident.phone.trim() || null,
        website: ident.website.trim() || null,
        // percent → basis points (18 → 1800, 12.5 → 1250, 5 → 500).
        gst_rate_bps: Math.round(gstPercent * 100),
      };
      const r = await client.put("/admin/settings/billing-identity", payload);
      const next = emptyIdentity();
      for (const f of IDENTITY_FIELDS) next[f] = r.data[f] || "";
      const bps = r.data.gst_rate_bps ?? 1800;
      next.gst_rate_percent = String(bps / 100);
      setIdent(next);
      setMeta({ configured: r.data.configured, gst_rate_percent: r.data.gst_rate_percent });
      setMessage("Billing identity saved. Applies to future facilitation invoices only — existing invoices are unchanged.");
    } catch (err) {
      setError(extractError(err, "Unable to save billing identity."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="section-block" data-testid="billing-identity-card">
      <div className="section-heading">
        <div>
          <p className="eyebrow">BILLING IDENTITY</p>
          <h3>Issuer of Barter India facilitation invoices</h3>
          <p className="muted small">
            These details are snapshotted onto <strong>each new</strong> facilitation
            invoice at creation time. Editing them later does not change historical
            invoices or PDFs.
            {meta && !meta.configured && (
              <span className="form-error" style={{ display: "block", marginTop: 6 }}
                     data-testid="billing-identity-incomplete-warning">
                ⚠ Required fields missing — facilitation invoices cannot be issued
                until this is complete.
              </span>
            )}
          </p>
        </div>
      </div>
      {error && <div className="form-error" data-testid="billing-identity-error">{error}</div>}
      {message && <div className="form-success" data-testid="billing-identity-success">{message}</div>}

      <form className="company-form" onSubmit={save} data-testid="billing-identity-form">
        <p className="eyebrow"><Building2 size={12} /> LEGAL &amp; TAX IDENTITY</p>
        <div className="form-grid">
          <label>Legal Business Name *
            <input required value={ident.legal_name} onChange={change("legal_name")}
                    data-testid="bi-legal-name" /></label>
          <label>Trade / Brand Name
            <input value={ident.brand_name} onChange={change("brand_name")}
                    data-testid="bi-brand-name" /></label>
          <label>PAN
            <input value={ident.pan} onChange={change("pan")}
                    maxLength={10} data-testid="bi-pan" /></label>
          <label>GSTIN *
            <input required value={ident.gstin} onChange={change("gstin")}
                    minLength={15} maxLength={15} data-testid="bi-gstin" /></label>
        </div>

        <p className="eyebrow" style={{ marginTop: 12 }}><MapPin size={12} /> BUSINESS ADDRESS</p>
        <div className="form-grid">
          <label>Address Line 1 *
            <input required value={ident.address_line1} onChange={change("address_line1")}
                    data-testid="bi-addr1" /></label>
          <label>Address Line 2
            <input value={ident.address_line2} onChange={change("address_line2")}
                    data-testid="bi-addr2" /></label>
          <label>City *
            <input required value={ident.city} onChange={change("city")}
                    data-testid="bi-city" /></label>
          <label>District
            <input value={ident.district} onChange={change("district")}
                    data-testid="bi-district" /></label>
          <label>State *
            <input required value={ident.state} onChange={change("state")}
                    data-testid="bi-state" /></label>
          <label>State Code *
            <input required value={ident.state_code} onChange={change("state_code")}
                    maxLength={2} data-testid="bi-state-code" /></label>
          <label>PIN Code *
            <input required value={ident.pin_code} onChange={change("pin_code")}
                    maxLength={6} data-testid="bi-pin" /></label>
          <label>Country *
            <input required value={ident.country} onChange={change("country")}
                    data-testid="bi-country" /></label>
        </div>

        <p className="eyebrow" style={{ marginTop: 12 }}><Mail size={12} /> CONTACT</p>
        <div className="form-grid">
          <label>Billing Email
            <input type="email" value={ident.email} onChange={change("email")}
                    data-testid="bi-email" /></label>
          <label>Billing Phone
            <input value={ident.phone} onChange={change("phone")}
                    data-testid="bi-phone" /></label>
          <label>Website
            <input value={ident.website} onChange={change("website")}
                    data-testid="bi-website" /></label>
        </div>

        <p className="eyebrow" style={{ marginTop: 12 }}><Percent size={12} /> TAX</p>
        <div className="form-grid">
          <label>GST Rate (%)
            <input type="number" min={0} max={100} step={0.01}
                    value={ident.gst_rate_percent}
                    onChange={change("gst_rate_percent")}
                    data-testid="bi-gst-percent" />
            <small className="muted">
              GST rate applied to new facilitation invoices. Changes apply to
              new invoices only.
            </small>
          </label>
        </div>

        <div className="form-actions">
          <button type="submit" className="primary-button" disabled={busy}
                   data-testid="save-billing-identity-button">
            <Save size={13} /> {busy ? "Saving…" : "Save billing identity"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminPlatformSettingsPage() {
  return (
    <div data-testid="admin-platform-settings-page">
      <BillingIdentityCard />
      <div style={{ height: 24 }} />
      <FacilitationFeeCard />
    </div>
  );
}
